/** Decode signature images into PDF-safe RGB pixels (browser canvas). */

export interface PdfImagePixels {
  width: number;
  height: number;
  rgb: Uint8Array;
}

export interface PngImageBytes {
  width: number;
  height: number;
  png: Uint8Array;
}

const CURSIVE_FONT = "'Brush Script MT', 'Segoe Script', 'Lucida Handwriting', cursive";

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to decode signature image'));
    img.src = src;
  });
}

export function base64ToUint8(base64: string): Uint8Array {
  const clean = base64.replace(/^data:[^;]+;base64,/, '').trim();
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)!;
  return bytes;
}

function canvasToPngBytes(canvas: HTMLCanvasElement): Uint8Array {
  return base64ToUint8(canvas.toDataURL('image/png'));
}

function canvasToRgb(canvas: HTMLCanvasElement): PdfImagePixels {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is unavailable');
  const { width, height } = canvas;
  const pixels = ctx.getImageData(0, 0, width, height).data;
  const rgb = new Uint8Array(width * height * 3);
  for (let i = 0; i < width * height; i++) {
    rgb[i * 3] = pixels[i * 4]!;
    rgb[i * 3 + 1] = pixels[i * 4 + 1]!;
    rgb[i * 3 + 2] = pixels[i * 4 + 2]!;
  }
  return { width, height, rgb };
}

/** Render a typed signature label to PNG bytes (matches SignatureCapture type mode). */
export function renderTypedSignatureToPngBytes(text: string): PngImageBytes {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is unavailable');

  const fontSize = 48;
  ctx.font = `${fontSize}px ${CURSIVE_FONT}`;
  const metrics = ctx.measureText(text);
  const padding = 16;
  canvas.width = Math.ceil(metrics.width) + padding * 2;
  canvas.height = Math.ceil(fontSize * 1.5) + padding * 2;

  ctx.font = `${fontSize}px ${CURSIVE_FONT}`;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#1a1a2e';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, padding, canvas.height / 2);

  return { width: canvas.width, height: canvas.height, png: canvasToPngBytes(canvas) };
}

/** Render a typed signature label to RGB pixels (matches SignatureCapture type mode). */
export function renderTypedSignatureToPdfRgb(text: string): PdfImagePixels {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is unavailable');

  const fontSize = 48;
  ctx.font = `${fontSize}px ${CURSIVE_FONT}`;
  const metrics = ctx.measureText(text);
  const padding = 16;
  canvas.width = Math.ceil(metrics.width) + padding * 2;
  canvas.height = Math.ceil(fontSize * 1.5) + padding * 2;

  ctx.font = `${fontSize}px ${CURSIVE_FONT}`;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#1a1a2e';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, padding, canvas.height / 2);

  return canvasToRgb(canvas);
}

export async function decodeBase64ImageToPngBytes(
  base64: string,
  mimeType = 'image/png',
): Promise<PngImageBytes> {
  const clean = base64.replace(/^data:[^;]+;base64,/, '').trim();
  if (!clean) throw new Error('Signature image data is empty');

  const img = await loadImage(`data:${mimeType};base64,${clean}`);
  const width = img.naturalWidth;
  const height = img.naturalHeight;
  if (width <= 0 || height <= 0) throw new Error('Signature image has invalid dimensions');

  if (mimeType === 'image/png' || mimeType.endsWith('png')) {
    return { width, height, png: base64ToUint8(clean) };
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is unavailable');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0);

  return { width, height, png: canvasToPngBytes(canvas) };
}

export async function decodeBase64ImageToPdfRgb(
  base64: string,
  mimeType = 'image/png',
): Promise<PdfImagePixels> {
  const clean = base64.replace(/^data:[^;]+;base64,/, '').trim();
  if (!clean) throw new Error('Signature image data is empty');

  const img = await loadImage(`data:${mimeType};base64,${clean}`);
  const width = img.naturalWidth;
  const height = img.naturalHeight;
  if (width <= 0 || height <= 0) throw new Error('Signature image has invalid dimensions');

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is unavailable');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0);

  return canvasToRgb(canvas);
}

/** @deprecated Prefer decodeBase64ImageToPdfRgb */
export async function decodeBase64ImageToPdfJpeg(
  base64: string,
  mimeType = 'image/png',
): Promise<{ width: number; height: number; jpeg: Uint8Array }> {
  const rgbImage = await decodeBase64ImageToPdfRgb(base64, mimeType);
  return { width: rgbImage.width, height: rgbImage.height, jpeg: rgbImage.rgb };
}

/** @deprecated Prefer renderTypedSignatureToPdfRgb */
export function renderTypedSignatureToPdfJpeg(text: string): {
  width: number;
  height: number;
  jpeg: Uint8Array;
} {
  const rgbImage = renderTypedSignatureToPdfRgb(text);
  return { width: rgbImage.width, height: rgbImage.height, jpeg: rgbImage.rgb };
}
